
import { CheckRunRecord } from '@mesa/core/checks';
import { useQuery as useZeroQuery } from '@rocicorp/zero/react';
import { queryOptions, useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { octokit, queryClient } from '@/lib/clients';
import { useZero } from '@/lib/zero';

export const GITHUB_ACTIONS_APP_ID = 15368;

const workflowRunIdFromUrl = (url: string) => {
  const match = url.match(/github\.com\/([^/]+)\/([^/]+)\/actions\/runs\/(\d+)/);
  if (!match) {
    throw new Error('Invalid workflow run URL');
  }
  return parseInt(match[3]);
};

const workflowJobIdFromUrl = (url: string) => {
  const match = url.match(/github\.com\/([^/]+)\/([^/]+)\/actions\/runs\/(\d+)\/job\/(\d+)/);
  if (!match) {
    throw new Error('Invalid workflow job URL');
  }
  return parseInt(match[4]);
};

export const checksForReferenceQuery = (owner: string, repo: string, ref: string) =>
  queryOptions({
    queryKey: [owner, repo, 'commits', ref, 'checks'],
    queryFn: async () => {
      const checks = await octokit.paginate(octokit.rest.checks.listForRef, {
        owner,
        repo,
        ref,
      });
      return checks;
    },
    staleTime: 1000 * 60 * 0.5,
  });

// export const workflowRunsQuery = (owner: string, repo: string, head_sha: string) =>
//   queryOptions({
//     queryKey: [owner, repo, 'actions', 'runs', head_sha],
//     queryFn: async () => {
//       const workflows = await octokit.paginate(octokit.rest.actions.listWorkflowRunsForRepo, {
//         owner,
//         repo,
//         head_sha,
//         exclude_pull_requests: true,
//         per_page: 100,
//       });
//       return workflows;
//     },
//   });

export const requiredStatusChecksQuery = (owner: string, repo: string, branch: string) =>
  queryOptions({
    queryKey: [owner, repo, 'branches', branch, 'protection', 'required_status_checks', 'contexts'],
    queryFn: async () => {
      const { data } = await octokit.rest.repos.getBranch({ owner, repo, branch });
      return data.protected ? data.protection.required_status_checks!.contexts : [];
    },
    retry: false,
    staleTime: 1000 * 60 * 60,
  });

export const useChecksMutations = (owner: string, repo: string, ref: string) => {
  const { data: checks } = useSuspenseQuery(checksForReferenceQuery(owner, repo, ref));
  // const { data: workflowRuns } = useSuspenseQuery(workflowRunsQuery(owner, repo, ref));

  const { mutate: reRunCheck } = useMutation({
    mutationFn: async (check: (typeof checks)[number]) => {
      if (check.app?.id === GITHUB_ACTIONS_APP_ID) {
        await octokit.rest.actions.reRunJobForWorkflowRun({
          owner,
          repo,
          job_id: workflowJobIdFromUrl(check.html_url!),
        });
      } else {
        await octokit.rest.checks.rerequestRun({ owner, repo, check_run_id: check.id });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: checksForReferenceQuery(owner, repo, ref).queryKey });
    },
  });

  const { mutate: reRunChecks } = useMutation({
    mutationFn: async () => {
      const actionsWorkflows = new Set(
        checks
          .filter((check) => check.app?.id === GITHUB_ACTIONS_APP_ID)
          .map((check) => workflowRunIdFromUrl(check.html_url!))
      );
      const otherCheckRunIds = checks
        .filter((check) => check.app?.id !== GITHUB_ACTIONS_APP_ID)
        .map((check) => check.id);

      if ([...actionsWorkflows].length > 0) {
        await Promise.all(
          [...actionsWorkflows].map((run_id) => octokit.rest.actions.reRunWorkflow({ owner, repo, run_id: run_id }))
        );
      }
      if (otherCheckRunIds.length > 0) {
        await Promise.all(
          otherCheckRunIds.map((check_run_id) =>
            octokit.rest.checks.rerequestRun({
              owner,
              repo,
              check_run_id,
            })
          )
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: checksForReferenceQuery(owner, repo, ref).queryKey });
    },
  });

  const { mutate: reRunFailedChecks } = useMutation({
    mutationFn: async () => {
      const failedChecks = checks.filter((check) => check.conclusion === 'failure');

      const actionsWorkflows = new Set(
        failedChecks
          .filter((check) => check.app?.id === GITHUB_ACTIONS_APP_ID)
          .map((check) => workflowRunIdFromUrl(check.html_url!))
      );
      const otherCheckRunIds = failedChecks
        .filter((check) => check.app?.id !== GITHUB_ACTIONS_APP_ID)
        .map((check) => check.id);

      if ([...actionsWorkflows].length > 0) {
        await Promise.all(
          [...actionsWorkflows].map((run_id) => octokit.rest.actions.reRunWorkflowFailedJobs({ owner, repo, run_id }))
        );
      }
      if (otherCheckRunIds.length > 0) {
        await Promise.all(
          otherCheckRunIds.map((check_run_id) =>
            octokit.rest.checks.rerequestRun({
              owner,
              repo,
              check_run_id,
            })
          )
        );
      }
    },
    onSuccess: () => {
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: checksForReferenceQuery(owner, repo, ref).queryKey });
      }, 2000);
    },
  });

  // const { mutate: rerunWorkflows } = useMutation({
  //   mutationFn: async () => {
  //     const workflowIds = [...new Set(workflowRuns.map((run) => run.workflow_id))];
  //     await Promise.all(workflowIds.map((run_id) => octokit.rest.actions.reRunWorkflow({ owner, repo, run_id })));
  //   },
  //   onSuccess: () => {
  //     queryClient.invalidateQueries({ queryKey: workflowRunsQuery(owner, repo, ref).queryKey });
  //   },
  // });

  // const { mutate: rerunWorkflowFailedJobs } = useMutation({
  //   mutationFn: async () => {
  //     const failedWorkflowRuns = workflowRuns.filter((run) => run.conclusion === 'failure').map((run) => run.id);
  //     await Promise.all(
  //       failedWorkflowRuns.map((run_id) => octokit.rest.actions.reRunWorkflowFailedJobs({ owner, repo, run_id }))
  //     );
  //   },
  //   onSuccess: () => {
  //     queryClient.invalidateQueries({ queryKey: workflowRunsQuery(owner, repo, ref).queryKey });
  //   },
  // });

  return { reRunCheck, reRunChecks, reRunFailedChecks };
};

export const useChecks = (owner: string, repo: string, sha: string) => {
  const z = useZero();
  const [allChecks] = useZeroQuery(
    z.query.CheckRunsTable.where(({ and, cmp }) =>
      and(cmp('owner', owner), cmp('repository', repo), cmp('headSha', sha))
    )
  );

  return useFilterLatestChecks(allChecks || []);
};

export const useFilterLatestChecks = (checks: CheckRunRecord[]) => {
  return useMemo(() => {
    const filtered = checks.filter((check) => check !== null);
    const latestChecks = new Map<string, (typeof filtered)[number]>();

    filtered.forEach((check) => {
      const key = `${check.checkSuiteId}-${check.name}`;
      const existing = latestChecks.get(key);

      if (!existing || new Date(check.startedAt) > new Date(existing.startedAt)) {
        latestChecks.set(key, check);
      }
    });

    return Array.from(latestChecks.values());
  }, [checks]);
};
